import React, { useState, useEffect } from 'react';
import '../styles/Profile.css';
import usePlans from '../hooks/usePlans';
import { TIER_DISPLAY, getTierLabel } from '../hooks/pricingData';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (saved) {
      const parsed = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0] || account?.username || account?.preferred_username || account?.email;
      const name = account?.name || account?.idTokenClaims?.name || account?.idTokenClaims?.given_name || account?.username?.split('@')[0] || '';
      const h = {};
      if (email) h['x-user-email'] = email;
      if (name)  h['x-user-name']  = name;
      return h;
    }
  } catch { /* ignore */ }
  return {};
}

function getRoleConfig(userRole) {
  let view = userRole?.viewMode || userRole?.activeView || null;
  if (!view && userRole?.caView) view = 'ca';

  if (view === 'ca')    return { label: 'CA / Auditor',    bg: '#3b7ef4', dot: '#6ba3f8' };
  if (view === 'admin') return { label: 'Admin',           bg: '#7C3AED', dot: '#9f67f5' };
  if (view === 'team')  return { label: 'CA Team Member',  bg: '#0891B2', dot: '#38bdf8' };
  if (view === 'client')return { label: 'Client',          bg: '#4A5568', dot: '#8896A8' };

  if (userRole?.isTeamMember) return { label: 'CA Team Member', bg: '#0891B2', dot: '#38bdf8' };
  if (userRole?.isCA)         return { label: 'CA / Auditor',   bg: '#3b7ef4', dot: '#6ba3f8' };
  if (userRole?.isAdmin)      return { label: 'Admin',          bg: '#7C3AED', dot: '#9f67f5' };
  return { label: 'Client', bg: '#4A5568', dot: '#8896A8' };
}

const NAV = [
  {
    id: 'account', label: 'Account', forRoles: ['all'],
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
  },
  {
    id: 'upgrade', label: 'Become a CA', forRoles: ['client'],
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
  },
  {
    id: 'access', label: 'My Access', forRoles: ['client', 'team'],
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
  },
  {
    id: 'invites', label: 'Team Invitations', forRoles: ['all'], badge: true,
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="23" y1="11" x2="17" y2="11" /><line x1="20" y1="8" x2="20" y2="14" /></svg>
  },
];

function ReportHeaderPreview({ firmName }) {
  const brand = firmName?.trim() || null;
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div className="pf-report-preview">
      <div className="pf-report-preview-row">
        <div>
          <div className="pf-report-preview-title">
            {brand
              ? brand
              : <><span className="pf-report-preview-brand">CA Audit </span><span className="pf-report-preview-accent">Pro</span></>
            }
          </div>
          <div className="pf-report-preview-sub">
            Client Company Name &nbsp;|&nbsp; FY 2024–25 &nbsp;|&nbsp; Data synced: {today}
          </div>
        </div>
        <div className="pf-report-preview-date">
          Issued: {today}
        </div>
      </div>
    </div>
  );
}

function FirmNameEditor({ userRole, onFirmNameSaved }) {
  const [editing,   setEditing]   = useState(false);
  const [firmInput, setFirmInput] = useState(userRole?.firm_name || '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const currentFirm = userRole?.firm_name || '';

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/users/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ firm_name: firmInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess('Firm name saved.');
      setEditing(false);
      onFirmNameSaved?.(data.firm_name);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pf-card">
      <div className="pf-card-title">Report Branding</div>
      <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
        Set your firm name to replace "CA Audit Pro" in the header of printed audit reports.
      </p>
      {success && <div className="pf-alert pf-alert-success" style={{ marginBottom: 10 }}>{success}</div>}
      {editing ? (
        <>
          {error && <div className="pf-alert pf-alert-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div className="pf-field" style={{ marginBottom: 10 }}>
            <label className="pf-label">Firm name (shown in report header)</label>
            <input
              type="text"
              maxLength={100}
              placeholder="e.g. Rajeev & Associates"
              className="pf-input"
              value={firmInput}
              onChange={e => setFirmInput(e.target.value)}
              autoFocus
            />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              {firmInput.length}/100 characters
            </div>
          </div>
          <ReportHeaderPreview firmName={firmInput} />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Preview of report header</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="pf-btn pf-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              className="pf-btn pf-btn-outline"
              onClick={() => { setEditing(false); setFirmInput(currentFirm); setError(''); }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="pf-row pf-row--no-border" style={{ alignItems: 'center', gap: 16 }}>
            <span className="pf-row-label" style={{ flexShrink: 0 }}>Firm name</span>
            <span className="pf-row-value" style={{ flex: 1 }}>
              {currentFirm || <em style={{ color: 'var(--text3)' }}>Not set — defaults to "CA Audit Pro"</em>}
            </span>
            <button
              className="pf-btn pf-btn-outline"
              style={{ padding: '4px 14px', fontSize: 12 }}
              onClick={() => { setFirmInput(currentFirm); setEditing(true); setError(''); }}
            >
              {currentFirm ? 'Edit' : 'Set'}
            </button>
          </div>
          <ReportHeaderPreview firmName={currentFirm} />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Preview of report header</div>
        </>
      )}
    </div>
  );
}

function AccountSection({ userRole, onSwitchAccount, onFirmNameSaved, plans }) {
  const { label, bg } = getRoleConfig(userRole);

  const plan = (plans || []).find(p => p.planCode === userRole?.subscriptionPlan)
    || userRole?.activePlan
    || null;

  const tierStyle = {
    starter:          { bg: 'var(--blue-bg)', color: 'var(--accent)' },
    growth:           { bg: 'var(--accent)',  color: '#fff' },
    enterprise:       { bg: '#0B0F1A',        color: '#fff' },
    client_starter:   { bg: 'var(--blue-bg)', color: 'var(--accent)' },
    client_growth:    { bg: 'var(--accent)',  color: '#fff' },
    client_growth_pro:{ bg: '#0B0F1A',        color: '#fff' },
  };
  const TIER_DISPLAY_LOCAL = {
    starter: 'Starter', growth: 'Growth', enterprise: 'Growth Pro',
    client_starter: 'Starter', client_growth: 'Growth', client_growth_pro: 'Growth Pro',
  };
  const ts = tierStyle[plan?.category] || tierStyle.starter;

  return (
    <>
      <div className="pf-page-title">Account details</div>
      <p className="pf-page-sub">Your identity and current role on AuditPrOS.</p>

      <div className="pf-card">
        <div className="pf-card-title">Identity</div>
        <div className="pf-row"><span className="pf-row-label">Name</span><span className="pf-row-value">{userRole?.name || '—'}</span></div>
        <div className="pf-row"><span className="pf-row-label">Email</span><span className="pf-row-value">{userRole?.email || '—'}</span></div>
        <div className="pf-row">
          <span className="pf-row-label">Role</span>
          <span
            className="pf-role-badge"
            style={{ background: bg + '22', color: bg, border: `1px solid ${bg}44` }}
          >
            <span className="pf-role-dot" style={{ background: bg }} />{label}
          </span>
        </div>
        {userRole?.icaiMemberId && (
          <div className="pf-row"><span className="pf-row-label">ICAI No.</span><span className="pf-row-value">{userRole.icaiMemberId}</span></div>
        )}
        {userRole?.icaiMemberId && (
          <div className="pf-row">
            <span className="pf-row-label">ICAI Verification</span>
            <span
              className="pf-row-value"
              style={{ color: userRole.icaiVerified ? 'var(--accent)' : 'var(--amber)' }}
            >
              {userRole.icaiVerified ? '✓ Verified' : '⏳ Pending'}
            </span>
          </div>
        )}
        {userRole?.pan_number && (
          <div className="pf-row">
            <span className="pf-row-label">PAN</span>
            <span className="pf-row-value pf-row-value-flex">
              <span className="pf-pan-value">{userRole.pan_number}</span>
              <span className="pf-pan-linked">✓ Linked</span>
            </span>
          </div>
        )}
      </div>

      {userRole?.teamMembership && (
        <div className="pf-card">
          <div className="pf-card-title">Team Membership</div>
          <div className="pf-row"><span className="pf-row-label">CA firm</span><span className="pf-row-value">{userRole.teamMembership.ca_name}</span></div>
          <div className="pf-row"><span className="pf-row-label">CA email</span><span className="pf-row-value">{userRole.teamMembership.ca_email}</span></div>
          <div className="pf-row">
            <span className="pf-row-label">Status</span>
            <span className="pf-row-value" style={{ color: 'var(--accent)' }}>Active member</span>
          </div>
        </div>
      )}

      {(userRole?.isCA || userRole?.isAdmin) && !userRole?.isTeamMember && (
        <FirmNameEditor userRole={userRole} onFirmNameSaved={onFirmNameSaved} />
      )}

      {plan && (
        <div className="pf-card">
          <div className="pf-card-title">Current subscription</div>
          <div className="pf-row">
            <span className="pf-row-label">Plan</span>
            <span className="pf-plan-pair">
              <span
                className="pf-tier-badge"
                style={{ background: ts.bg, color: ts.color }}
              >
                {TIER_DISPLAY_LOCAL[plan.category] || plan.category}
              </span>
              <span className="pf-plan-name-inline">{plan.name}</span>
            </span>
          </div>
          <div className="pf-row">
            <span className="pf-row-label">Price</span>
            <span className="pf-row-value pf-price-value">
              {plan.priceDisplay}
              <span className="pf-price-unit">
                {plan.priceMonthly === 0 ? ' / forever' : ` / ${plan.durationUnit}`}
              </span>
            </span>
          </div>
          <div className="pf-row">
            <span className="pf-row-label">Clients</span>
            <span className="pf-row-value">
              {plan.maxClientsActivated === 'Unlimited' ? 'Unlimited' : `Up to ${plan.maxClientsActivated}`}
            </span>
          </div>
          <div className="pf-row">
            <span className="pf-row-label">Team logins</span>
            <span className="pf-row-value">{plan.maxTeamMembersActivated}</span>
          </div>
          <div className="pf-row pf-row--no-border">
            <span className="pf-row-label">Status</span>
            <span
              className="pf-status-indicator"
              style={{ color: userRole?.subscriptionPlan ? 'var(--green)' : 'var(--amber)' }}
            >
              {userRole?.subscriptionPlan ? '● Active' : '● Free tier'}
            </span>
          </div>
        </div>
      )}

      <div className="pf-action-row">
        <button className="pf-btn pf-btn-outline pf-action-btn" onClick={onSwitchAccount}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          Switch account
        </button>
        <button className="pf-btn pf-btn-danger pf-action-btn" onClick={() => { localStorage.removeItem('auditpros_user'); window.location.reload(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          Sign out
        </button>
      </div>
    </>
  );
}

function SubscriptionSection({ userInfo, userRole, onPlanSelected, caPlans, clientPlans }) {
  const isCA = userRole?.isCA || userRole?.isAdmin;
  const allowedPlans = isCA ? caPlans : clientPlans;

  const activePlanCode = userRole?.subscriptionPlan || null;
  const [selectedPlanCode, setSelectedPlanCode] = useState(activePlanCode);

  useEffect(() => {
    setSelectedPlanCode(userRole?.subscriptionPlan || null);
  }, [userRole?.subscriptionPlan]);

  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  if (userRole?.isTeamMember) {
    return (
      <>
        <div className="pf-page-title">Subscription</div>
        <p className="pf-page-sub">Subscription is managed by your CA firm.</p>
        <div className="pf-alert pf-alert-warn">
          Your CA manages the subscription for this account. Contact your CA firm to change the plan.
        </div>
      </>
    );
  }

  const handleSelect = async (plan) => {
    if (plan.planCode === selectedPlanCode) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/subscriptions/me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ plan_code: plan.planCode, unit_count: 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSelectedPlanCode(plan.planCode);
      setSuccess(`${plan.name} activated successfully.`);
      onPlanSelected?.({ ...userRole, subscriptionPlan: plan.planCode, activePlan: plan });
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const ctaClass = { free: 'pf-cta-free', blue: 'pf-cta-blue', dark: 'pf-cta-dark' };

  return (
    <>
      <div className="pf-page-title">Subscription</div>
      <p className="pf-page-sub">
        {isCA
          ? 'Choose the CA plan that fits your practice. All plans include the full audit rule set.'
          : 'Choose the Client plan for your business. Includes Tally sync and internal team access.'}
      </p>

      {error   && <div className="pf-alert pf-alert-error">{error}</div>}
      {success && <div className="pf-alert pf-alert-success">{success}</div>}

      <div className="pf-plans-grid">
        {allowedPlans.map(plan => {
          const isActive = plan.planCode === selectedPlanCode;
          return (
            <div key={plan.planCode} className={`pf-pc${plan.featured ? ' featured' : ''}`}>
              <span className={`pf-pc-tier tier-${plan.category}`}>
                {TIER_DISPLAY[plan.category] || plan.category}
              </span>
              <div className="pf-pc-name">{plan.name}</div>
              <p className="pf-pc-caption">{plan.caption}</p>
              <div className="pf-pc-price">
                {plan.priceDisplay}
                {plan.priceMonthly === 0
                  ? <span> / forever</span>
                  : <span> / {plan.durationUnit}</span>}
              </div>
              <div className={`pf-pc-meta${plan.featured ? ' fm' : ''}`}>
                {plan.maxClientsActivated === 'Unlimited' ? 'Unlimited' : `Up to ${plan.maxClientsActivated}`} clients
                {' · '}
                {plan.maxTeamMembersActivated === 'Unlimited' ? 'Unlimited' : plan.maxTeamMembersActivated} team {plan.maxTeamMembersActivated === 1 ? 'login' : 'logins'}
              </div>
              <button
                className={`pf-pc-cta ${isActive ? 'pf-cta-green' : (ctaClass[plan.ctaStyle] || 'pf-cta-blue')}`}
                onClick={() => handleSelect(plan)}
                disabled={saving || isActive}
                style={isActive ? { outline: '2px solid var(--accent)', outlineOffset: 2 } : {}}
              >
                {saving && plan.planCode === selectedPlanCode ? '⏳ Updating…'
                  : isActive ? '✓ Current Plan'
                    : plan.cta}
              </button>
              <div className="pf-pc-body">
                <div className="pf-pc-slabel">What's included</div>
                {plan.ruleList.map(r => <div className="pf-pc-feat" key={r}><span className="ck">✓</span>{r}</div>)}
                {plan.ruleListDisabled?.length > 0 && <>
                  <div className="pf-pc-slabel">Not included</div>
                  {plan.ruleListDisabled.map(r => <div className="pf-pc-feat dim" key={r}><span className="lk">—</span>{r}</div>)}
                </>}
              </div>
            </div>
          );
        })}
      </div>

      <p className="pf-sub-footnote">
        * Billing integration (Razorpay) coming soon. Plan changes take effect immediately.
        Contact support for invoicing or enterprise pricing.
      </p>
    </>
  );
}

function UpgradeSection({ userRole, onRoleUpgrade }) {
  const [icaiInput, setIcaiInput] = useState('');
  const [files,     setFiles]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const pending = userRole?.upgradeRequest || null;

  const handleFileChange = (e) => {
    const sel = Array.from(e.target.files);
    const valid = sel.filter(f => ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type));
    if (valid.length !== sel.length) { setError('Only PDF, JPG and PNG files are allowed.'); return; }
    setFiles(valid); setError('');
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    const c = icaiInput.trim();
    if (!/^\d{6}$/.test(c)) { setError('ICAI membership number must be exactly 6 digits.'); return; }
    if (files.length === 0) { setError('Please upload at least one supporting document.'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('icaiMemberId', c);
      files.forEach(f => fd.append('documents', f));
      const res = await fetch(`${BASE}/users/upgrade-request`, { method: 'POST', headers: getAuthHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess(data.message || 'Request submitted successfully.');
      setIcaiInput(''); setFiles([]);
      onRoleUpgrade?.({ ...userRole, upgradeRequest: { status: 'pending', icai_member_id: c } });
    } catch (err) { setError(err.message || 'Failed to submit request.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="pf-page-title">Become a CA on AuditPrOS</div>
      <p className="pf-page-sub">Submit your ICAI membership number and supporting documents. Our team verifies your credentials and upgrades your account within 1–2 business days.</p>
      {pending ? (
        <div className="pf-card">
          <div className="pf-alert pf-alert-warn" style={{ margin: 0 }}>
            <div className="pf-pending-content">
              <span className="pf-pending-icon">⏳</span>
              <div>
                <div className="pf-pending-title">Upgrade request pending review</div>
                <div>ICAI number <strong>{pending.icai_member_id}</strong> submitted. Our team will review within 1–2 business days.</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pf-card">
          {error   && <div className="pf-alert pf-alert-error">{error}</div>}
          {success && <div className="pf-alert pf-alert-success">{success}</div>}
          <div className="pf-field">
            <label className="pf-label">ICAI membership number *</label>
            <input
              type="text" inputMode="numeric" maxLength={6}
              placeholder="e.g. 123456" className="pf-input"
              value={icaiInput}
              onChange={e => setIcaiInput(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="pf-field">
            <label className="pf-label">Supporting documents * — PDF, JPG or PNG, max 5 MB each</label>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="pf-file-input" onChange={handleFileChange} />
            {files.length > 0 && (
              <div className="pf-file-list">
                {files.map((f, i) => (
                  <div key={i} className="pf-file-item">
                    <span>📄</span><span>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className="pf-btn pf-btn-primary pf-btn-full"
            onClick={handleSubmit}
            disabled={loading || icaiInput.length !== 6 || files.length === 0}
          >
            {loading ? <span className="pf-spinner" /> : null}
            {loading ? 'Submitting…' : 'Submit CA Upgrade Request'}
          </button>
        </div>
      )}
      <div className="pf-card" style={{ marginTop: 0 }}>
        <div className="pf-card-title">What happens after you submit</div>
        {[
          { n: '01', t: 'Our team manually verifies your ICAI membership number against the official registry.' },
          { n: '02', t: "You'll receive an email confirmation within 1–2 business days when your account is upgraded." },
          { n: '03', t: 'Once upgraded, you can onboard clients, upload Tally data, run audit checks, and generate reports.' },
        ].map(s => (
          <div key={s.n} className="pf-row" style={{ gap: 12, justifyContent: 'flex-start' }}>
            <span className="pf-step-num">{s.n}</span>
            <span className="pf-step-text">{s.t}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function AccessSection({ userRole }) {
  const pan = userRole?.pan_number || null;
  return (
    <>
      <div className="pf-page-title">My Access</div>
      <p className="pf-page-sub">Your access to AuditPrOS sections is configured by the CA firm managing your audit.</p>
      <div className="pf-card">
        <div className="pf-card-title">Client Identity (PAN)</div>
        {pan ? (
          <>
            <div className="pf-row">
              <span className="pf-row-label">PAN</span>
              <span className="pf-row-value pf-pan-value">{pan}</span>
            </div>
            <div className="pf-row">
              <span className="pf-row-label">Status</span>
              <span className="pf-row-value" style={{ color: 'var(--accent)' }}>✓ Linked — your CA can identify your account</span>
            </div>
            <p className="pf-pan-note">
              Your PAN matches your account to your CA's client records and Tally companies. It cannot be changed — contact your CA if there is an error.
            </p>
          </>
        ) : (
          <div className="pf-alert pf-alert-warn" style={{ marginBottom: 0 }}>
            <strong>PAN not set.</strong> Go to your Dashboard and enter your PAN to see the Tally companies and audit data your CA has linked to your account.
          </div>
        )}
      </div>
      <div className="pf-card">
        <div className="pf-client-wrap">
          <div className="pf-client-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="pf-client-title">Access managed by your CA</div>
          <p className="pf-client-sub">If you cannot view a report, raise a query, or access a section, your CA may not have granted access yet.</p>
          <div className="pf-client-steps">
            <div className="pf-client-steps-title">What to do if you have an access issue</div>
            {[
              'Contact your CA or audit manager and specify which section you cannot access.',
              'Ask them to check your role assignment under their AuditPrOS team settings.',
              'If the issue persists, ask them to contact AuditPrOS support on your behalf.',
            ].map((t, i) => (
              <div key={i} className="pf-client-step">
                <span className="pf-client-step-arrow">→</span><span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function InvitesSection({ userRole, onRoleUpgrade }) {
  const [inviteLoading, setInviteLoading] = useState({});
  const [inviteMsg,     setInviteMsg]     = useState({});
  const pendingInvites = userRole?.pendingInvites || [];

  const handleAction = async (inviteId, action) => {
    setInviteLoading(p => ({ ...p, [inviteId]: true }));
    try {
      const res = await fetch(`${BASE}/users/team/${action}/${inviteId}`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setInviteMsg(p => ({ ...p, [inviteId]: action === 'accept' ? '✓ Joined team' : 'Declined' }));
      onRoleUpgrade?.({ ...userRole, pendingInvites: pendingInvites.filter(i => i.id !== inviteId) });
    } catch (err) { setInviteMsg(p => ({ ...p, [inviteId]: '✗ ' + err.message })); }
    finally { setInviteLoading(p => ({ ...p, [inviteId]: false })); }
  };

  return (
    <>
      <div className="pf-page-title">Team Invitations</div>
      <p className="pf-page-sub">CA firms can invite you to join their audit team. Accept to become a team member.</p>
      {pendingInvites.length === 0
        ? <div className="pf-alert pf-alert-info">No pending team invitations.</div>
        : (
          <div className="pf-card">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="pf-invite">
                <div className="pf-invite-ca">{invite.ca_name}</div>
                <div className="pf-invite-email">{invite.ca_email} has invited you to join their audit team.</div>
                {inviteMsg[invite.id]
                  ? (
                    <div style={{
                      fontSize: 13,
                      color: inviteMsg[invite.id].startsWith('✓') ? 'var(--accent)' : 'var(--red)',
                      fontWeight: 500,
                    }}>
                      {inviteMsg[invite.id]}
                    </div>
                  ) : (
                    <div className="pf-invite-actions">
                      <button className="pf-btn pf-btn-primary" onClick={() => handleAction(invite.id, 'accept')} disabled={inviteLoading[invite.id]}>
                        {inviteLoading[invite.id] ? <span className="pf-spinner" /> : '✓ Accept'}
                      </button>
                      <button className="pf-btn pf-btn-danger" onClick={() => handleAction(invite.id, 'reject')} disabled={inviteLoading[invite.id]}>
                        Decline
                      </button>
                    </div>
                  )
                }
              </div>
            ))}
          </div>
        )
      }
    </>
  );
}

export default function Profile({ userInfo, userRole: userRoleProp, onClose, onRoleUpgrade, onSwitchAccount }) {
  const { plans, caPlans, clientPlans } = usePlans();
  const account  = userInfo?.account;
  const rawName  = account?.name || account?.idTokenClaims?.name || account?.username?.split('@')[0] || '';
  const rawEmail = account?.idTokenClaims?.email || account?.idTokenClaims?.preferred_username
    || account?.idTokenClaims?.emails?.[0] || account?.username || account?.preferred_username || '';

  const [userRole, setUserRole] = useState(() => {
    if (userRoleProp) return userRoleProp;
    const isCA = userInfo?.method === 'gmail';
    return {
      isCA, isAdmin: false, isTeamMember: false,
      name: rawName || (userInfo?.mobile ? `+91 ${userInfo.mobile}` : 'User'),
      email: rawEmail || (userInfo?.mobile ? `+91 ${userInfo.mobile}` : ''),
      icaiMemberId: null, icaiVerified: false, upgradeRequest: null,
      pendingInvites: [], teamMembership: null,
      subscriptionPlan: null, subscriptionFeatures: null,
    };
  });
  useEffect(() => { if (userRoleProp) setUserRole(userRoleProp); }, [userRoleProp]);

  const handleRoleUpgrade = (updated) => { setUserRole(updated); onRoleUpgrade?.(updated); };

  const effectiveIsCA = userRole?.viewMode === 'ca'
    || userRole?.viewMode === 'admin'
    || userRole?.isCA
    || userRole?.isAdmin;
  const isCA = effectiveIsCA;
  const inviteCount = userRole?.pendingInvites?.length || 0;

  const visibleNav = NAV.filter(item => {
    if (item.forRoles.includes('all')) return true;
    if (isCA && item.forRoles.includes('ca')) return true;
    if (!isCA && item.forRoles.includes('client')) return true;
    if (userRole?.isTeamMember && item.forRoles.includes('team')) return true;
    return false;
  });

  const [activePage, setActivePage] = useState('account');

  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail;
      if (typeof tab === 'string' && tab) setActivePage(tab);
    };
    window.addEventListener('audit-pros-profile-tab', handler);
    return () => window.removeEventListener('audit-pros-profile-tab', handler);
  }, []);

  const displayName  = userRole?.name || rawName || 'User';
  const displayEmail = userRole?.email || rawEmail || '';
  const displayInit  = displayName[0]?.toUpperCase() || 'U';
  const { label, bg } = getRoleConfig(userRole);

  return (
    <>
      <div className="pf-overlay" onClick={e => e.target === e.currentTarget && onClose?.()}>
        <div className="pf-modal">
          <div className="pf-sidebar">
            <div className="pf-sidebar-top">
              <div className="pf-avatar">{displayInit}</div>
              <div className="pf-user-name">{displayName}</div>
              {displayEmail && <div className="pf-user-email">{displayEmail}</div>}
              <span
                className="pf-role-badge"
                style={{ background: bg + '22', color: bg, border: `1px solid ${bg}44` }}
              >
                <span className="pf-role-dot" style={{ background: bg }} />{label}
              </span>
            </div>
            <nav className="pf-nav">
              <div className="pf-nav-section">Account</div>
              {visibleNav.map(item => (
                <button
                  key={item.id}
                  className={`pf-nav-item${activePage === item.id ? ' active' : ''}`}
                  onClick={() => setActivePage(item.id)}
                >
                  {item.icon}{item.label}
                  {item.badge && inviteCount > 0 && <span className="pf-nav-badge">{inviteCount}</span>}
                </button>
              ))}
            </nav>
            <div className="pf-sidebar-actions">
              <button className="pf-sidebar-btn pf-sidebar-btn-switch" onClick={onSwitchAccount}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                Switch account
              </button>
              <button className="pf-sidebar-btn pf-sidebar-btn-signout" onClick={() => { localStorage.removeItem('auditpros_user'); window.location.reload(); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                Sign out
              </button>
            </div>
          </div>

          <div className="pf-main">
            <button className="pf-close" onClick={onClose} title="Close">✕</button>
            {activePage === 'account'      && <AccountSection userRole={userRole} onSwitchAccount={onSwitchAccount} onFirmNameSaved={(name) => handleRoleUpgrade({ ...userRole, firm_name: name })} plans={plans} />}
            {activePage === 'upgrade'      && <UpgradeSection userRole={userRole} onRoleUpgrade={handleRoleUpgrade} />}
            {activePage === 'access'       && <AccessSection userRole={userRole} />}
            {activePage === 'invites'      && <InvitesSection userRole={userRole} onRoleUpgrade={handleRoleUpgrade} />}
          </div>
        </div>
      </div>
    </>
  );
}
